import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Package2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';

/**
 * Reusable searchable asset selector.
 *
 * Drop-in replacement for the plain Shadcn <Select> previously used for
 * "pick an asset" across InsightsPage, WorkOrders, Defects, Maintenance,
 * Documents, etc. Adds:
 *   - Type-ahead search across unit_number / name / serial / make / model
 *   - Renders via Radix Portal so it never clips behind sibling cards
 *   - Optional `allowAll` to surface an "All assets" option (null value)
 *
 * Props:
 *   assets:      Array<{ id, name, unit_number, serial_number, make, model, status }>
 *   value:       string | null   (selected asset id; null = "All")
 *   onChange:    (id: string | null) => void
 *   placeholder: string (default: "Select an asset...")
 *   allowAll:    boolean (default: false)
 *   allLabel:    string (default: "All assets")
 *   className:   wrapper className for the trigger button
 *   disabled:    boolean
 *   testId:      data-testid (default: "asset-combobox")
 */
const AssetCombobox = ({
  assets = [],
  value,
  onChange,
  placeholder = 'Select an asset...',
  allowAll = false,
  allLabel = 'All assets',
  className,
  disabled = false,
  testId = 'asset-combobox',
}) => {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => assets.find((a) => a.id === value) || null,
    [assets, value]
  );

  const renderLabel = (a) => {
    if (!a) return null;
    const unit = a.unit_number || a.name || a.serial_number;
    const makeModel = [a.make, a.model].filter(Boolean).join(' ');
    return makeModel ? `${unit} — ${makeModel}` : unit;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between font-normal text-left',
            !selected && !value && 'text-muted-foreground',
            className
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            <Package2 className="w-4 h-4 shrink-0 opacity-60" />
            <span className="truncate">
              {selected
                ? renderLabel(selected)
                : value === null && allowAll
                ? allLabel
                : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]"
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={true} data-testid={`${testId}-popover`}>
          <CommandInput
            placeholder="Search assets…"
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>No matching assets.</CommandEmpty>
            {allowAll && (
              <CommandGroup>
                <CommandItem
                  value="__all__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-all`}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === null ? 'opacity-100 text-[#FFD108]' : 'opacity-0')} />
                  {allLabel}
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={assets.length > 0 ? `Assets (${assets.length})` : undefined}>
              {assets.map((a) => {
                const unit = a.unit_number || a.name || a.serial_number || a.id;
                const sub = [a.make, a.model].filter(Boolean).join(' ');
                // Build a haystack so CMDK fuzzy-matches all metadata
                const keywords = [unit, a.name, a.serial_number, a.make, a.model, a.status]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <CommandItem
                    key={a.id}
                    value={`${unit} ${keywords}`}
                    onSelect={() => {
                      onChange(a.id);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-option-${a.id}`}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === a.id ? 'opacity-100 text-[#FFD108]' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{unit}</span>
                      {(sub || a.serial_number) && (
                        <span className="text-xs text-muted-foreground truncate">
                          {sub}
                          {sub && a.serial_number ? ' · ' : ''}
                          {a.serial_number ? <span className="font-mono">{a.serial_number}</span> : null}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AssetCombobox;
